import { animate, style, transition, trigger } from '@angular/animations';
import { HttpHeaders } from '@angular/common/http';
import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import {
  BrowserControlCommunicationService,
  GmSltService,
  PdfService,
  ServiceVisitRecordService,
  WorkOrderDocumentService,
} from '@applications/sha-services';
import { EventTypes } from '@models/server-common';
import { Actions, ofType } from '@ngrx/effects';
import { TypedAction } from '@ngrx/store/src/models';
import { ApiService } from '@quorum/api';
import { AuthenticationStateService } from '@quorum/authentication/services';
import { DeepCopyPipe, DeepEqualsPipe } from '@quorum/common-pipes';
import { CommunicatorFacade } from '@quorum/communicator-ngrx';
import { GMServiceVisitByDealerQueryParameters, GMServiceVisitByVinQueryParameters } from '@quorum/models/oem-query';
import { GmSltData, ServiceVisitRecord } from '@quorum/models/xs-misc';
import { ConversationQueryParameters, UserQueryParameters } from '@quorum/models/xs-query';
import {
  Address,
  Conversation,
  Employee,
  Event,
  Media,
  Member,
  OemRooftopLink,
  QuoteLine,
  ServiceAppointment,
  SystemControlValue,
  TokenUser,
  VehicleInspectionReportHeader,
  VehicleVipInspectionCategory,
  WorkOrder,
} from '@quorum/models/xs-resource';
import { OemMenuService } from '@quorum/quorum-api';
import { ServiceLaneDropdownsFacade } from '@quorum/service-lane-dropdowns/facade';
import { ServiceLaneFacade } from '@quorum/service-lane-root/facade';
import { TwoActionDialogComponent, TwoActionDialogData } from '@quorum/sha-components-ng-ui';
import { deleteMediaFromServerSuccess } from '@quorum/sha-media';
import { status, SvcVehicleDiagnosticsRootFacade } from '@quorum/svc-vehicle-diagnostics-root';
import { SystemControlStateService } from '@quorum/xsr-system-control/services';
import { AuthenticatedUser } from 'libs/authentication/src/lib/+state/authentication.interfaces';
import { MediaStateService } from 'libs/sha-media/src/lib/media-state.service';
import { BehaviorSubject, combineLatest, Observable, of, Subject, Subscription } from 'rxjs';
import { filter, map, switchMap, take, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { environment } from '@quorum/environments';
import { DUAL_REAR_TIRES_CATEGORY_NAME, InspectionService } from '../inspection.service';
import { InspectionReportPdfService } from '../inspection-report-pdf/inspection-report-pdf.service';
import { isInspectionPdfPreviewEnabled } from '../inspection-report-pdf/preview-gate';
import { requiresExternalPdfViewer } from './pdf-printing';

// Raised when an in-flight save can no longer complete because the inspection
// was cleared (deleted) or the component was torn down. This is an expected,
// user-initiated race — not a failure — so callers treat it as a benign no-op
// instead of logging it to the console (and, in turn, to monitoring).
class InspectionSaveAbortedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InspectionSaveAbortedError';
    // Restore the prototype chain so `instanceof` still works when a consuming
    // app down-levels this lib to ES5.
    Object.setPrototypeOf(this, InspectionSaveAbortedError.prototype);
  }
}

type PdfPrintTarget = { type: 'inline' } | { type: 'external' };

@Component({
  selector: 'quorum-inspection',
  templateUrl: './inspection.component.html',
  styleUrls: ['./inspection.component.scss'],
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [style({ opacity: 0 }), animate('100ms', style({ opacity: 1 }))]),
      transition(':leave', [style({ opacity: 1 }), animate('100ms', style({ opacity: 0 }))]),
    ]),
  ],
})
export class InspectionComponent implements OnInit, OnDestroy {
  @Input() transactions$: Observable<{
    appointment: ServiceAppointment;
    workOrder: WorkOrder;
  }>;
  vehicleDiagnostics$: Observable<{ enabled: boolean; dealerCountryCode: string; oem: string }>;
  destroy$ = new Subject();
  // The load/create chains are untracked promises, so they can resolve after this
  // component is gone and InspectionService.resetService() has cleared the shared state.
  private isDestroyed = false;
  beforeUnload: any = null;
  firstOpenFlag: any;
  serverError = false;
  workOrderPostedStatus = false;
  quote: any;
  disableQuote: boolean;
  quoteId: any;
  oemrtId: any;
  vehicleId: any;
  showButton: any;
  workOrder$: BehaviorSubject<WorkOrder> = new BehaviorSubject(null);
  workOrderId: any;
  dashboardEnabled: any;
  tireTreads: any;
  brakeMeasurements: any;
  inspectionTypes: any;
  activeTab = 0;
  displayInspectionSelection = false;
  selectedInspection: any;
  xselleratorEmployeeId: string;
  inspectionLogicSubscription$: Subscription;
  selectedFile: any;
  taskIdToLinkMedia: number;
  authenticatedUser$: Observable<AuthenticatedUser>;
  oem$: Observable<OemRooftopLink>;
  mediaParams$: Observable<any>;
  media$: Observable<Media[]>;
  printingReport = false;
  deleteInspectionSub: Subscription;
  disableSaveNotify = false;
  displaySaviIconFlags$: Observable<{ gmOem: boolean; vehicleDiagnosticsEnabled: boolean }>;
  inpsectionTiresCollapsed = false;
  walkAroundTiresCollapsed = false;
  onStarDiagnosticsCollapsed = false;
  showActionsMenu = false;
  showTiresOptions = [false, false, false, false, false]; // each entry is associated with the same array index of the task
  gmSltData$: Observable<GmSltData>;
  serviceVisitRecord$: Observable<ServiceVisitRecord>;
  documentId: string;
  @ViewChild('printContainer') printContainer: ElementRef;
  inspectionDeleted = false;
  private pendingPdfPrintCleanup?: () => void;

  mediaDeleted$: Observable<
    {
      media: Media;
    } & TypedAction<'[Media] Delete Media From Server Success'>
  > = this.actions$.pipe(ofType(deleteMediaFromServerSuccess));

  tokenUser$: Observable<TokenUser> = this.authenticationStateService.selectAuthenticatedUser().pipe(
    filter((authUser) => authUser !== null),
    map((authUser) => authUser.user),
    take(1)
  );

  oemRooftopId$: Observable<string> = this.authenticationStateService.selectAuthenticatedEmployee().pipe(
    filter((employee) => employee !== null),
    switchMap((employee) =>
      this.apiService
        .get(`v/1/system/oem-rooftop-links/${employee.rooftopId}`)
        .pipe(map((link) => link?.associateId ?? null))
    ),
    take(1)
  );

  dealerCountryCode$: Observable<SystemControlValue> = this.authenticationStateService
    .selectAuthenticatedEmployee()
    .pipe(
      filter((employee) => employee !== null),
      switchMap((employee) =>
        this.systemControlStateService.getSystemControlValues(['CL_DEALER_COUNTRY_ID'], employee.rooftopId)
      ),
      take(1),
      map((dcc) => dcc['CL_DEALER_COUNTRY_ID'])
    );

  readonly inspectionPdfPreviewEnabled = isInspectionPdfPreviewEnabled(environment.environment);

  constructor(
    public inspectionService: InspectionService,
    private authenticationStateService: AuthenticationStateService,
    private systemControlStateService: SystemControlStateService,
    private route: ActivatedRoute,
    private apiService: ApiService,
    private dialog: MatDialog,
    private browserControlService: BrowserControlCommunicationService,
    private matIconRegistry: MatIconRegistry,
    private domSanitizer: DomSanitizer,
    private snackBar: MatSnackBar,
    public serviceLaneDropdownFacade: ServiceLaneDropdownsFacade,
    public mediaStateService: MediaStateService,
    public serviceLaneFacade: ServiceLaneFacade,
    private communicatorFacade: CommunicatorFacade,
    public svcVehicleDiagnosticsRootFacade: SvcVehicleDiagnosticsRootFacade,
    private oemMenuService: OemMenuService,
    private gmSltService: GmSltService,
    private workOrderDocumentService: WorkOrderDocumentService,
    private pdfService: PdfService,
    private actions$: Actions,
    private serviceVisitRecordService: ServiceVisitRecordService,
    private inspectionReportPdfService: InspectionReportPdfService
  ) {}

  ngOnDestroy() {
    this.isDestroyed = true;
    this.pendingPdfPrintCleanup?.();

    if (this.inspectionLogicSubscription$) {
      this.inspectionLogicSubscription$.unsubscribe();
    }
    if (this.deleteInspectionSub) {
      this.deleteInspectionSub.unsubscribe();
    }
    this.destroy$.next();
    this.destroy$.complete();
    this.inspectionService.resetService();
  }

  ngOnInit() {
    if (this.browserControlService.embeddedInXsellerator) {
      /**
       * TODO: This block would ideally live on the gm-slt service but
       * due to circular dependency problems within the repo it is not advised
       * (also found on appointment/menu component)
       */
      this.gmSltData$ = combineLatest([
        this.inspectionService.workOrderId$.pipe(
          filter((workOrderId) => workOrderId),
          take(1)
        ),
        this.authenticationStateService.selectAuthenticatedUser().pipe(
          filter((authenticatedUser) => authenticatedUser !== null),
          map((authUser) => authUser.user),
          take(1)
        ),
        this.authenticationStateService.selectAuthenticatedEmployee().pipe(
          filter((employee) => employee !== null),
          switchMap((employee) => {
            return this.apiService.get(`v/1/system/oem-rooftop-links/${employee.rooftopId}`).pipe(
              map((oemRooftopLink) => {
                return oemRooftopLink?.associateId ?? null;
              })
            );
          }),
          take(1)
        ),
      ]).pipe(
        switchMap(([workOrderId, authenticatedUser, oem]) => {
          if (oem === 'GM') {
            return this.gmSltService.getOrCreateGmSltData(authenticatedUser.destination, workOrderId.toString());
          } else {
            return of(null);
          }
        })
      );
    }

    this.serviceVisitRecord$ = combineLatest([
      this.inspectionService.workOrderId$.pipe(
        filter((workOrderId) => workOrderId),
        take(1)
      ),
      this.authenticationStateService.selectAuthenticatedUser().pipe(
        filter((authenticatedUser) => authenticatedUser !== null),
        map((authUser) => authUser.user),
        take(1)
      ),
    ]).pipe(
      switchMap(([workOrderId, authenticatedUser]) => {
        return this.serviceVisitRecordService.getOrCreateServiceVisitRecord(
          authenticatedUser.destination,
          this.route.snapshot.queryParams.appointmentId?.toString(),
          workOrderId?.toString()
        );
      }),
      switchMap((svr: ServiceVisitRecord) => {
        if (svr.communications?.advisorMobileCheckIn) {
          return of(svr);
        }
        return this.serviceVisitRecordService.recordAdvisorMobileCheckIn(svr);
      }),
      switchMap((svr: ServiceVisitRecord) => {
        return this.serviceVisitRecordService.recordInitialSnapshot(svr);
      })
    );
    this.serviceVisitRecord$.pipe(takeUntil(this.destroy$)).subscribe();

    this.mediaParams$ = this.mediaStateService.getMediaParamsForKey('inspection');
    this.authenticatedUser$ = this.authenticationStateService.selectAuthenticatedUser();
    this.inspectionService.inspection$.subscribe((inspection) => {
      if (this.inspectionService.inspectionBackup && inspection) {
        this.unsavedChangesCheck(this.inspectionService.inspectionBackup, inspection);
      }
    });
    this.inspectionService.listenForToasts().subscribe((toastOptions) => {
      this.snackBar.open(toastOptions.message, toastOptions.actionText, toastOptions.options);
    });
    //implement the unsaved stuff
    this.registerInspectionIcons();

    this.vehicleDiagnostics$ = this.authenticationStateService.selectAuthenticatedEmployee().pipe(
      filter((emp) => emp != null),
      take(1),
      switchMap((employee) => {
        return combineLatest([
          this.systemControlStateService.getSystemControlValues(
            ['CL_OEM_VEHICLE_DIAGNOSTIC_DATA_ENABLED', 'CL_DEALER_COUNTRY_ID'],
            employee.rooftopId
          ),
          this.apiService.get(`v/1/system/oem-rooftop-links/${employee.rooftopId}`),
        ]).pipe(
          map(([scts, oemRooftopLink]) => {
            return {
              enabled: scts['CL_OEM_VEHICLE_DIAGNOSTIC_DATA_ENABLED'].value === '1' ? true : false,
              dealerCountryCode:
                scts['CL_DEALER_COUNTRY_ID'].value === 'CAN'
                  ? 'CA'
                  : scts['CL_DEALER_COUNTRY_ID'].value === 'USA'
                  ? 'US'
                  : 'US',
              oem: oemRooftopLink?.associateId ?? null,
            };
          })
        );
      })
    );

    if (this.transactions$) {
      combineLatest([this.transactions$, this.vehicleDiagnostics$])
        .pipe(
          takeUntil(this.destroy$),
          map(([transactions, vehicleDiagnostics]) => {
            switch (vehicleDiagnostics.oem) {
              case 'GM':
                if (vehicleDiagnostics.enabled) {
                  combineLatest([
                    this.oemMenuService.businessId$.pipe(
                      filter((businessId) => businessId != null),
                      take(1)
                    ),
                    this.authenticationStateService.selectAuthenticatedUser().pipe(
                      tap((authenticatedUser) => {
                        this.oemMenuService.initialize(authenticatedUser.user.destination);
                      })
                    ),
                    this.svcVehicleDiagnosticsRootFacade.dealerBac$,
                  ])
                    .pipe(take(1))
                    .subscribe(([businessId, authenticatedUser, dealerBac]) => {
                      const oneYearAgo = new Date();
                      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
                      oneYearAgo.setHours(0, 0, 0);

                      const today = new Date();
                      today.setHours(23, 59, 59);

                      if (this.serviceLaneFacade.isLeapYear(today)) {
                        oneYearAgo.setDate(oneYearAgo.getDate() + 1);
                      }

                      this.svcVehicleDiagnosticsRootFacade.getGMServiceVisitByVin(
                        businessId,
                        vehicleDiagnostics.dealerCountryCode,
                        transactions.workOrder.embedded?.vehicle?.vin,
                        new GMServiceVisitByVinQueryParameters({
                          startDate: oneYearAgo.toISOString(),
                          endDate: today.toISOString(),
                          bac: dealerBac,
                          destinationId: authenticatedUser.user.destination,
                        })
                      );

                      this.svcVehicleDiagnosticsRootFacade.getGMServiceVisitByDealer(
                        businessId,
                        vehicleDiagnostics.dealerCountryCode,
                        oneYearAgo.toISOString(),
                        today.toISOString(),
                        dealerBac,
                        new GMServiceVisitByDealerQueryParameters({
                          destinationId: authenticatedUser.user.destination,
                        })
                      );

                      let transactionId: number;
                      let transactionType: string;

                      if (transactions.workOrder.statusId === 'R') {
                        transactionId = transactions.appointment.id;
                        transactionType = 'appointment';
                      } else {
                        transactionId = transactions.workOrder.id;
                        transactionType = 'workOrder';
                      }

                      this.svcVehicleDiagnosticsRootFacade
                        .gmActionListener(
                          businessId,
                          vehicleDiagnostics.dealerCountryCode,
                          transactions.workOrder.embedded?.vehicle?.vin,
                          transactionId,
                          transactionType,
                          false
                        )
                        .pipe(takeUntil(this.destroy$))
                        .subscribe();
                    });
                }
                break;
              default:
                break;
            }
          })
        )
        .subscribe();
    }

    this.inspectionLogicSubscription$ = this.inspectionService.triggerInspectionLogic
      //.pipe(takeLast(1))
      .subscribe((workOrderId) => {
        this.authenticationStateService
          .selectAuthenticatedEmployee()
          .pipe(filter((employee) => employee != null))
          .subscribe((employee: Employee) => {
            if (employee.jobTitleId == 'SAD') {
              this.disableSaveNotify = true;
            }
            if (employee.jobTitleId == 'TCH') {
              this.activeTab = 0;
            } else {
              this.activeTab = 1;
            }

            this.displaySaviIconFlags$ = combineLatest([
              this.apiService
                .get(`/v/1/system/oem-rooftop-links/${employee.rooftopId}`)
                .pipe(map((oemRooftopLink: OemRooftopLink) => oemRooftopLink.associateId)),
              this.systemControlStateService.getSystemControlValues(
                ['CL_OEM_VEHICLE_DIAGNOSTIC_DATA_ENABLED'],
                employee.rooftopId
              ),
            ]).pipe(
              map(([oem, vehicleDiagnosticsEnabled]) => {
                return {
                  gmOem: oem === 'GM',
                  vehicleDiagnosticsEnabled:
                    vehicleDiagnosticsEnabled.CL_OEM_VEHICLE_DIAGNOSTIC_DATA_ENABLED?.value === '1',
                };
              })
            );
          });

        this.workOrderId = workOrderId;
        this.inspectionService.getBodyReport(workOrderId).then((bodyReport) => {
          this.workOrder$.next(bodyReport.workOrder);
          /* Check if any data returned for Body Report */
          if (JSON.parse(bodyReport.data) || JSON.parse(bodyReport.data).length) {
            /* Check if this data is in new format object (with signature) or not and assign values to bodyReportObject accordingly */
            this.inspectionService.bodyReportObject.bodyReportElements = JSON.parse(bodyReport.data).bodyReport
              ? JSON.parse(bodyReport.data).bodyReport
              : JSON.parse(bodyReport.data);

            this.inspectionService.bodyReportObject.signature = JSON.parse(bodyReport.data).signature
              ? JSON.parse(bodyReport.data).signature
              : null;

            if (JSON.parse(bodyReport.data).vehicleDirty) {
              this.inspectionService.bodyReportObject.vehicleDirty = JSON.parse(bodyReport.data).vehicleDirty;
            }

            this.inspectionService.bodyReportObject.bodyReportElements.forEach(function (
              element: any,
              index: number,
              array: any[]
            ) {
              array[index].f = 0;
            });
          }
        });

        this.inspectionService.getQuote(workOrderId).then((quote) => {
          this.quote = quote;
          this.inspectionService.workOrderId$.next(workOrderId);
          this.inspectionService.quoteId$.next(quote.QuoteId);
          this.disableQuote = false;
          this.quoteId = quote.QuoteId;
          this.oemrtId = quote.OemrtId;
          this.vehicleId = quote.VehicleId;
          this.inspectionService.appointmentId = this.route.parent.snapshot.params.appointmentId;
          this.inspectionService.vehicleId = this.vehicleId;
          this.inspectionService.workOrderId = workOrderId;
          this.inspectionService.quoteId = quote.QuoteId;
          this.workOrderPostedStatus = quote.WorkOrderPostedStatus;
          this.getTreadDepthSettings(this.oemrtId);
          this.getBrakeMeasurementValues(this.oemrtId);
          const self = this;

          this.oem$ = this.apiService.get(`/v/1/system/oem-rooftop-links/${quote.OemrtId}`);

          combineLatest([
            this.systemControlStateService
              .getSystemControlValues(['CL_GM_DASHBOARD_INTEGRATION_ENABLED'], this.oemrtId)
              .pipe(
                map((sct) => sct['CL_GM_DASHBOARD_INTEGRATION_ENABLED'].value),
                take(1)
              ),
            this.oem$.pipe(take(1)),
            this.serviceLaneFacade.inspectionDeletedPreviously$.pipe(take(1)),
          ])
            .pipe(takeUntil(this.destroy$))
            .subscribe(([sctValue, oem, inspectionDeletedPreviously]) => {
              self.dashboardEnabled = sctValue === '1' ? true : false;
              self.getTireTreadValues(self.oemrtId);
              self.authenticationStateService
                .selectAuthenticatedUser()
                .pipe(take(1))
                .subscribe((user: AuthenticatedUser) => {
                  self.xselleratorEmployeeId = user.user.xselleratorEmployeeId;
                  self.getInspectionDetails(
                    self.workOrderId,
                    self.vehicleId,
                    user.user.xselleratorEmployeeId,
                    user.user.destination,
                    oem,
                    inspectionDeletedPreviously
                  );
                });
            });

          if (this.quote.CustomerChanged === true) {
            // vm.$mdDialog.show(
            //   vm.$mdDialog.alert()
            //     .clickOutsideToClose(true)
            //     .title('Vehicle Owner Changed')
            //     .textContent('The owner on the vehicle has changed since the quote was first created, please verify that all parts and labour pricing are correct.')
            //     .ariaLabel('Vehicle Owner Changed')
            //     .ok('Ok')
            // );
          }
        });
      });
  }

  registerInspectionIcons() {
    this.matIconRegistry.addSvgIcon(
      `car_brakes`,
      this.domSanitizer.bypassSecurityTrustResourceUrl(`assets/inspection-icons/icons8-brake-warning-50.svg`)
    );

    this.matIconRegistry.addSvgIcon(
      `car`,
      this.domSanitizer.bypassSecurityTrustResourceUrl(`assets/inspection-icons/icons8-car-50.svg`)
    );
    this.matIconRegistry.addSvgIcon(
      `car_battery`,
      this.domSanitizer.bypassSecurityTrustResourceUrl(`assets/inspection-icons/icons8-car-battery-50.svg`)
    );
    this.matIconRegistry.addSvgIcon(
      `car_radiator`,
      this.domSanitizer.bypassSecurityTrustResourceUrl(`assets/inspection-icons/icons8-car-radiator-50.svg`)
    );
    this.matIconRegistry.addSvgIcon(
      `car_service`,
      this.domSanitizer.bypassSecurityTrustResourceUrl(`assets/inspection-icons/icons8-car-radiator-50.svg`)
    );
    this.matIconRegistry.addSvgIcon(
      `car_engine_oil`,
      this.domSanitizer.bypassSecurityTrustResourceUrl(`assets/inspection-icons/icons8-engine-oil-50.svg`)
    );
    this.matIconRegistry.addSvgIcon(
      `car_headlights`,
      this.domSanitizer.bypassSecurityTrustResourceUrl(`assets/inspection-icons/icons8-headlight-50.svg`)
    );
    this.matIconRegistry.addSvgIcon(
      `car_piston`,
      this.domSanitizer.bypassSecurityTrustResourceUrl(`assets/inspection-icons/icons8-piston-50.svg`)
    );
    this.matIconRegistry.addSvgIcon(
      `car_speedometer`,
      this.domSanitizer.bypassSecurityTrustResourceUrl(`assets/inspection-icons/icons8-speedometer-50.svg`)
    );
    this.matIconRegistry.addSvgIcon(
      `car_steering-wheel`,
      this.domSanitizer.bypassSecurityTrustResourceUrl(`assets/inspection-icons/icons8-steering-wheel-50.svg`)
    );
    this.matIconRegistry.addSvgIcon(
      `car_tire_pressure`,
      this.domSanitizer.bypassSecurityTrustResourceUrl(`assets/inspection-icons/icons8-tire_pressure-50.svg`)
    );
    this.matIconRegistry.addSvgIcon(
      `car_tire`,
      this.domSanitizer.bypassSecurityTrustResourceUrl(`assets/inspection-icons/icons8-wheel-50.svg`)
    );
  }

  printVCR(workOrder: WorkOrder) {
    if (this.inspectionService.unsaved) {
      const dialogData: TwoActionDialogData = {
        actionTwoCaption: 'Save',
        actionOneCaption: 'Cancel',
        content: 'Unsaved changes will not display on the printed report. Would you like to save the inspection?',
        title: 'Save Inspection',
      };

      const dialogRef = this.dialog.open(TwoActionDialogComponent, {
        width: '400px',
        data: dialogData,
        disableClose: true,
      });

      dialogRef
        .afterClosed()
        .pipe(takeUntil(this.destroy$))
        .subscribe((result: boolean) => {
          if (result) {
            this.updateTasks(false, workOrder)
              .then(() => {
                this.printingReport = true;
                this.snackBar.open('Preparing report for printing..', null, { duration: 5000 });
                this.runPrintFlow(5000);
              })
              .catch((err) => this.logUnlessSaveAborted('Save before print failed:', err));
          }
        });
    } else {
      this.printingReport = true;
      this.snackBar.open('Preparing report for printing..', null, { duration: 3500 });
      this.runPrintFlow(3500);
    }
  }

  private runPrintFlow(delayMs: number): void {
    setTimeout(() => {
      const storage = document.getElementById('storage');
      if (!storage) {
        console.warn('Cannot print VCR report: #storage element not found.');
        this.printingReport = false;
        return;
      }

      // Defensive: a previous print that didn't reach afterprint (e.g. user navigated away
      // mid-print) could have left this node behind.
      document.getElementById('TempVcrReport')?.remove();

      const div = document.createElement('div');
      div.className = 'printMe';
      div.innerHTML = storage.innerHTML;
      div.id = 'TempVcrReport';
      document.body.appendChild(div);

      const onAfterPrint = (): void => {
        this.printingReport = false;
        div.remove();
      };
      window.addEventListener('afterprint', onAfterPrint, { once: true });

      window.print();
    }, delayMs);
  }

  categoryOmitUnselected(category: any) {
    var self = this;
    category.tasks.forEach((task: any) => {
      if (task.greenBox === false && task.yellowBox === false && task.redBox === false && task.greyBox === false) {
        task.greyBox === true;
        self.inspectionService.onTaskActionChange('grey', task);
      }
    });
  }

  tireButtonGroupChange(value: any, task: any) {
    var self = this;
    switch (value) {
      case 'greyBox':
        self.inspectionService.toggleTaskOmit(task);
        break;
      case 'yellowBox':
        task.yellowBox = !task.yellowBox;
        break;
      case 'redBox':
        task.redBox = !task.redBox;
        break;
      case 'greenBox':
        task.greenBox = !task.greenBox;
        break;
    }
  }

  unsavedChangesCheck(backupInspection: any, currentInspection: any) {
    var vm = this;
    //DeepCopyPipe Turns all date objects to strings, current needs this for a proper comparison
    const jsonParsedCurrentInspection = new DeepCopyPipe().transform(currentInspection);
    if (new DeepEqualsPipe().transform(backupInspection, jsonParsedCurrentInspection) === false) {
      vm.inspectionService.unsaved = true;
    } else {
      vm.inspectionService.unsaved = false;
    }
  }

  closingCheck(event: any) {
    return new Promise((resolve, reject) => {
      resolve(true);
    });
  }

  getInspectionDetails(
    workOrderId: any,
    vehicleId: any,
    xselleratorEmployeeId: any,
    destinationId: string,
    oem: OemRooftopLink,
    inspectionDeletedPreviously: boolean
  ) {
    let self = this;
    if (this.inspectionService.inspection == null || Object.keys(this.inspectionService.inspection).length === 0) {
      this.inspectionService.isLoading = true;
      this.inspectionService.showButton = true;
      this.inspectionService.inspectionDeleteFlag = true;

      // query for WO inspection
      this.apiService
        .get(`/v/2/vip/inspections/${workOrderId}`)
        .toPromise()
        .then((data) => {
          if (self.isDestroyed) {
            return;
          }
          self.inspectionService.inspection = data ? data : null;

          // WO inspection exists, proceed
          if (self.inspectionService.inspection) {
            this.mediaStateService.setMediaModelKey({
              vehicleId: this.inspectionService.vehicleId,
              appointmentId: this.inspectionService.appointmentId,
              workOrderId: this.inspectionService.workOrderId,
              quoteId: this.inspectionService.quoteId,
              destinationId: destinationId,
              inspectionId: self.inspectionService.inspection.inspectionTypeId,
              customerFacing: true,
              key: 'inspection',
            });
            self.inspectionService.getOnstar(self.vehicleId).then(function (onStar) {
              if (self.isDestroyed || !self.inspectionService.inspection) {
                return;
              }
              self.inspectionService.inspection.onStar = onStar && onStar.length > 0 ? onStar[0] : {};
              self.createInspectionObjects();
              self.setInspectionDeleteFlag();

              self.inspectionService.inspection.currentUser = xselleratorEmployeeId;
              self.inspectionService.inspection.oemrtId = self.oemrtId;
              self.inspectionService.inspection.quote = self.quote;
              self.inspectionService.inspection.addYellowToQuote = false;

              if (!self.browserControlService.embeddedInXsellerator) {
                self.inspectionService.inspection.completedInApp = 1;
              } else {
                self.inspectionService.inspection.completedInApp = 0;
              }
              self.inspectionService.inspectionBackup = new DeepCopyPipe().transform(self.inspectionService.inspection);
            });

            self.inspectionService
              .getVcrReportData(self.inspectionService.workOrderId, self.inspectionService.quoteId)
              .then(function (report) {
                //self.report = report;
              });
            this.inspectionService.savingCheck(); // check incomplete tasks on nspection load to display the banners
            self.populateInspectionWithVehicleDiagnosticsData();
          } else {
            /**
             * Inspection does not exist, query for available inspection types for dealer
             */
            self
              .getVin(vehicleId)
              .then((vin) => {
                if (vin) {
                  return self.getFuelType(vin);
                }
                return null;
              })
              .then((fuelType) => {
                self.inspectionService.fuelType = fuelType;
                return self.inspectionService.getInspectionTypes(oem, fuelType);
              })
              .then((inspectionTypes) => {
                self.inspectionTypes = inspectionTypes;
                for (let i = inspectionTypes.length - 1; i >= 0; i--) {
                  if (inspectionTypes[i].name === 'Multi-Point Vehicle Health Check') {
                    inspectionTypes[i].name = 'GM Multi-Point Vehicle Health Check';
                  }
                }
                /**
                 * If there is only 1 inspection type, display the list and auto create inspection if an inspection has never been deleted
                 * TODO: implement error message for 0 inspection types
                 */
                if (inspectionTypes.length <= 1) {
                  self.selectedInspection = inspectionTypes[0].id;
                  inspectionDeletedPreviously
                    ? ''
                    : self.createInspection(inspectionTypes[0].id, workOrderId, xselleratorEmployeeId, destinationId);
                  self.displayInspectionSelection = inspectionDeletedPreviously ? true : false;
                } else {
                  self.displayInspectionSelection = true;
                }
              });
          }
        });
    } else {
      this.inspectionService.inspectionBackup = new DeepCopyPipe().transform(this.inspectionService.inspection);
    }
  }

  getVin(vehicleId: string): Promise<string | null> {
    return this.apiService
      .get(`/v/2/vehicles/vehicles/${vehicleId}`)
      .toPromise()
      .then((data) => {
        if (data?.vin) {
          return data.vin;
        } else {
          console.warn('VIN not found for vehicle: ', vehicleId);
          return null;
        }
      })
      .catch((error) => {
        console.error('Error fetching vehicle data:', error);
        return null;
      });
  }

  getFuelType(vin: string): Promise<string | null> {
    return this.apiService
      .get(`/v/2/vehicles/vin-decode/${vin}/fuel-type`)
      .toPromise()
      .then((data) => {
        if (data?.fuelType) {
          return data.fuelType.toLowerCase() === 'electric' ? 'EV' : 'ICE';
        } else {
          console.warn('No fuelType found for VIN: ', vin);
          return null;
        }
      })
      .catch((error) => {
        console.error('Error fetching vehicle data:', error);
        return null;
      });
  }

  getTireTreadValues(oemrtId: any) {
    let self = this;
    this.inspectionService.getTireTreadValues(oemrtId).then(function (tireDetails: any) {
      self.tireTreads = tireDetails;
    });
  }

  getBrakeMeasurementValues(oemrtId: any) {
    let self = this;
    this.inspectionService.getBrakeMeasurementValues(oemrtId).then(function (brakeDetails) {
      self.brakeMeasurements = brakeDetails;
    });
  }

  createInspection(inspectionType: any, workOrderId: any, xselleratorEmployeeId: any, destinationId: string) {
    var self = this;
    this.inspectionDeleted = false;
    self.inpsectionTiresCollapsed = false;
    self.walkAroundTiresCollapsed = false;
    self.inspectionService.isLoading = true;
    this.mediaStateService.setMediaModelKey({
      vehicleId: this.inspectionService.vehicleId,
      appointmentId: this.inspectionService.appointmentId,
      workOrderId: this.inspectionService.workOrderId,
      quoteId: this.inspectionService.quoteId,
      destinationId: destinationId,
      inspectionId: inspectionType,
      customerFacing: true,
      key: 'inspection',
    });
    self.apiService
      .post(`v/2/vip/inspections/${workOrderId}/${inspectionType}`, this.inspectionService.inspection)
      .toPromise()
      .then((inspection: any) => {
        if (self.isDestroyed) {
          return;
        }
        self.inspectionService.inspection = inspection;
        self.inspectionService.getOnstar(self.vehicleId).then(function (onStar: any) {
          if (self.isDestroyed || !self.inspectionService.inspection) {
            return;
          }
          self.inspectionService.inspection.onStar = onStar && onStar.length > 0 ? onStar[0] : {};
          self.createInspectionObjects();
          self.setInspectionDeleteFlag();
          self.inspectionService.inspection.currentUser = xselleratorEmployeeId;
          self.inspectionService.inspection.oemrtId = self.oemrtId;
          self.inspectionService.inspection.quote = self.quote;
          self.inspectionService.inspection.addYellowToQuote = false;
          if (!self.browserControlService.embeddedInXsellerator) {
            self.inspectionService.inspection.completedInApp = 1;
          } else {
            self.inspectionService.inspection.completedInApp = 0;
          }
          self.inspectionService.inspectionBackup = new DeepCopyPipe().transform(self.inspectionService.inspection);
          self.populateInspectionWithVehicleDiagnosticsData();
          // PLEASE KEEP FOR FUTURE USE
          // self.generateInspectionPdf('storage', ['#quoteHeader', '#quoteContainer', '#quoteTotal'], 500, true);
        });
      });
  }

  handleBodyReportSignatureCompleted(signature: any) {
    this.inspectionService.bodyReportObject.signature = signature;
  }

  sendBrowserMessage(): void {
    // send message to parent
    window.parent.postMessage({ type: 'inspectionSaved' }, '*');
  }

  async onSaveClicked(workOrder: WorkOrder) {
    try {
      await this.updateTasks(false, workOrder);
      this.sendBrowserMessage();
    } catch (err) {
      this.logUnlessSaveAborted('Update tasks failed:', err);
    }
  }

  // A save that aborts because the inspection was cleared or the component was
  // destroyed mid-flight is an expected race, not a failure — so it must not be
  // reported to the console (and, through it, to monitoring). Genuine failures
  // still surface.
  private logUnlessSaveAborted(context: string, err: unknown): void {
    if (err instanceof InspectionSaveAbortedError) {
      return;
    }
    console.error(context, err);
  }

  updateTasks(skipYellowCountCheck: boolean, workOrder: WorkOrder): Promise<boolean> {
    const self = this;
    return new Promise<boolean>((resolve, reject) => {
      // `addYellowToQuoteChoice === null` means no dialog was shown for this save
      // (no yellow items, or programmatic auto-save), so the prior flag value is
      // preserved. When the dialog was shown, we always set the flag explicitly so
      // a previous "Yes" can't leak into a current "No".
      const persistInspection = (addYellowToQuoteChoice: boolean | null): void => {
        if (!self.inspectionService.inspection) {
          reject(new InspectionSaveAbortedError('Inspection was cleared before save could complete.'));
          return;
        }
        if (addYellowToQuoteChoice !== null) {
          self.inspectionService.inspection.addYellowToQuote = addYellowToQuoteChoice;
        }
        self.apiService
          .put(`v/2/vip/inspections/${self.inspectionService.workOrderId}`, self.inspectionService.inspection)
          .toPromise()
          .then(() => {
            if (!self.inspectionService.inspection) {
              reject(new InspectionSaveAbortedError('Inspection was cleared while save was in flight.'));
              return;
            }
            self.inspectionService.inspectionBackup = new DeepCopyPipe().transform(self.inspectionService.inspection);
            self.unsavedChangesCheck(self.inspectionService.inspectionBackup, self.inspectionService.inspection);

            const toastOptions: { message: string; actionText: string | null; options: { duration: number } } = {
              message:
                self.inspectionService.inspection.addYellowToQuote === true
                  ? `Any red or yellow items have been added to the quote.`
                  : `Any red items have been added to the quote.`,
              actionText: null,
              options: { duration: 3000 },
            };

            self.inspectionService.addToast(toastOptions);
            self.inspectionService.inspectionSavedNotification.next(true);
            self.setInspectionDeleteFlag();
            self.inspectionService
              .getVcrReportData(self.inspectionService.workOrderId, self.inspectionService.quoteId)
              .catch((err) => console.error('getVcrReportData failed:', err));
            self.inspectionService
              .saveBodyReport(
                workOrder,
                self.inspectionService.bodyReportObject.bodyReportElements,
                self.inspectionService.bodyReportObject.signature,
                self.inspectionService.bodyReportObject.vehicleDirty
              )
              .then(() => {
                self.inspectionService.unsaved = false;
              })
              .catch((err) => console.error('saveBodyReport failed:', err));
            resolve(true);
          })
          .catch(reject);
      };

      self.inspectionService
        .savingCheck()
        .then(() => self.inspectionService.getYellowActionCount())
        .then((data: any) => {
          if (!self.inspectionService.inspection) {
            reject(new InspectionSaveAbortedError('Inspection was cleared before save could start.'));
            return;
          }
          workOrder.walkaroundComplete = !self.inspectionService.walkAroundIncomplete;
          workOrder.inspectionComplete = !self.inspectionService.incomplete;

          if (data.length >= 1 && skipYellowCountCheck === false) {
            const dialogData: TwoActionDialogData = {
              actionTwoCaption: 'Yes',
              actionOneCaption: 'No',
              content: 'Would you like to add all yellow items to the quote as well?',
              title: 'Quote Items',
            };

            const dialogRef = self.dialog.open(TwoActionDialogComponent, {
              width: '400px',
              data: dialogData,
              disableClose: true,
            });

            let dialogAnswered = false;
            dialogRef
              .afterClosed()
              .pipe(takeUntil(self.destroy$))
              .subscribe({
                next: (result: boolean) => {
                  dialogAnswered = true;
                  persistInspection(result === true);
                },
                error: reject,
                complete: () => {
                  // takeUntil tore the stream down before the dialog emitted —
                  // settle the outer Promise so awaiting callers don't hang.
                  if (!dialogAnswered) {
                    reject(
                      new InspectionSaveAbortedError(
                        'Inspection component destroyed while quote-items dialog was open.'
                      )
                    );
                  }
                },
              });
          } else {
            persistInspection(null);
          }
        })
        .catch(reject);
    });
  }

  openBodyWindow() {}

  openQuoteWindow() {}

  openReport() {
    this.inspectionService.leavingInspection().then(function (response: any) {
      if (response) {
        //vm.$state.go('vip.vcrreport', { id: vm.workOrderId, quoteId: vm.quoteId, reportType: vm.inspectionService.inspection.reportType });
      }
    });
  }

  openBodyReport() {
    var vm = this;
    vm.inspectionService.leavingInspection().then(function (response) {
      if (response) {
        //Go to body window
        //vm.$state.go('vip.bodyreport', { id: vm.workOrderId });
      }
    });
  }

  parseCategories(cat: any) {
    if (typeof cat === 'string') {
      return JSON.parse(cat);
    } else {
      let tempString = '';
      cat.forEach(function (element: any) {
        tempString += element;
      }, this);

      return JSON.parse(tempString);
    }
  }

  setInspectionDeleteFlag() {
    if (this.inspectionService.inspection.categories) {
      this.inspectionService.inspection.categories.forEach(function (element: any) {
        element.tasks.forEach(function (task: any) {
          if (task.inserted === true) {
            this.inspectionService.inspectionDeleteFlag = false;
          }
        }, this);
      }, this); // jshint ignore:line
    }

    if (this.inspectionService.inspection.tires) {
      this.inspectionService.inspection.tires.forEach(function (element: any) {
        element.tasks.forEach(function (task: any) {
          if (task.inserted === true) {
            this.inspectionService.inspectionDeleteFlag = false;
          }
        }, this);
      }, this); // jshint ignore:line
    }

    if (this.inspectionService.inspection.tireOtherCategories) {
      this.inspectionService.inspection.tireOtherCategories.forEach(function (element: any) {
        element.tasks.forEach(function (task: any) {
          if (task.inserted === true) {
            this.inspectionService.inspectionDeleteFlag = false;
          }
        }, this);
      }, this); // jshint ignore:line
    }
  }

  async onDeleteClicked(oem: OemRooftopLink, workOrderId: number) {
    try {
      await this.deleteInspection(oem, workOrderId);
      this.sendBrowserMessage();
    } catch (err) {
      console.error('Delete inspection failed:', err);
    }
  }

  deleteInspection(oem: OemRooftopLink, workOrderId: number): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const dialogData: TwoActionDialogData = {
        actionTwoCaption: 'Yes',
        actionOneCaption: 'No',
        content: 'Would you like to delete this inspection and all associated media?',
        title: 'Delete Inspection',
      };

      const dialogRef = this.dialog.open(TwoActionDialogComponent, {
        width: '400px',
        data: dialogData,
      });

      let vehicleId: string | null;
      let fuelType: string | null;

      let fuelTypePromise: Promise<string | null>;

      if (!this.inspectionService.fuelType) {
        fuelTypePromise = this.inspectionService
          .getQuote(workOrderId)
          .then((quote) => {
            vehicleId = quote.VehicleId;
            return this.getVin(vehicleId);
          })
          .then((vin) => {
            if (vin) {
              return this.getFuelType(vin);
            }
            return null;
          });
      } else {
        fuelTypePromise = Promise.resolve(this.inspectionService.fuelType);
      }

      fuelTypePromise.then((resolvedFuelType) => {
        fuelType = resolvedFuelType;
      });

      dialogRef.afterClosed().subscribe((result: boolean) => {
        if (!result) {
          resolve(false); // user cancelled
          return;
        }

        if (this.documentId) {
          this.workOrderDocumentService.deleteWorkOrderDocument(workOrderId.toString(), this.documentId).subscribe();
        }

        this.inspectionService.deleteInspection(workOrderId).then(() => {
          this.inspectionDeleted = true;
          this.inspectionService.inspection = null;
          this.inspectionService.incomplete = false;
          this.inspectionService.walkAroundIncomplete = false;
          this.inspectionService.requiredCategory = {};
          this.inspectionService.walkAroundRequiredCategory = {};
          this.inspectionService.requiredTasks = {};
          this.inspectionService.walkAroundRequiredTasks = {};
          this.inspectionService.bodyReportObject.signature = null;
          this.inspectionService.bodyReportObject.vehicleDirty = false;
          this.inspectionService.vehicleDiagnosticsId = null;
          this.inspectionService.report = {
            header: { expressFlag: true },
          };
          this.documentId = null;

          this.inspectionService.getInspectionTypes(oem, fuelType).then((inspectionTypes) => {
            this.inspectionTypes = inspectionTypes;
            for (let i = inspectionTypes.length - 1; i >= 0; i--) {
              if (inspectionTypes[i].name === 'Multi-Point Vehicle Health Check') {
                inspectionTypes[i].name = 'GM Multi-Point Vehicle Health Check';
              }
            }

            this.serviceLaneFacade.inspectionDeletedPreviously$.next(true);

            if (inspectionTypes.length <= 1) {
              this.selectedInspection = inspectionTypes[0].id;
            }
            this.displayInspectionSelection = true;

            this.deleteInspectionSub = combineLatest([
              this.mediaStateService.getWorkOrderMedia(workOrderId),
              this.serviceLaneFacade.getWorkOrderMPVIQuoteLines(workOrderId),
            ])
              .pipe(
                map(([media, quoteLines]) => {
                  return { media, quoteLines };
                }),
                take(1)
              )
              .subscribe((data) => {
                data.media.forEach((media) => {
                  this.mediaStateService.deleteMedia(media);
                });
                data.quoteLines.forEach((quoteLine) => {
                  this.serviceLaneFacade.deleteQuoteLineFromServer(quoteLine);
                });
              });

            const toastOptions: any = {
              message: 'Inspection deleted.',
              actionText: null,
              options: { duration: 3000 },
            };
            this.inspectionService.addToast(toastOptions);

            resolve(true); // ✅ finished successfully
          });
        });
      });
    });
  }

  getTreadDepthSettings(oemrtId: any) {
    var self = this;
    this.inspectionService.getTreadDepthSettings(oemrtId).then(function (result) {
      self.inspectionService.treadDepthSettings = result;
    });
  }

  createInspectionObjects() {
    var self = this;
    const windshieldChecks = ['WINDSHIELD_CRACKS', 'WINDSHIELD_CHIPS', 'Q_WINDSHIELD_CHIPS', 'Q_WINDSHIELD_CRACKS'];
    const tireOtherCategoriesChecks = [
      'SPARE_CHECK',
      'TIRE_DOT',
      'TIRE_INFLATION',
      'TIRE_SEALANT_EXPIRATION',
      'Q_SPARE_CHECK',
      'Q_TIRE_DOT',
      'Q_TIRE_INFLATION',
    ];
    const tireOtherChecks = [
      'ALIGNMENT_NEEDED',
      'BALANCE_NEEDED',
      'ROTATION_NEEDED',
      'Q_ALIGNMENT_NEEDED',
      'Q_BALANCE_NEEDED',
      'Q_ROTATION_NEEDED',
    ];
    const tireOtherGmChecks = [
      'WHEEL_LUG_NUT_TORQUE_CHECK',
      'RESET_TIRE_PRESSURE_MONITOR',
      'Q_WHEEL_LUG_NUT_TORQUE_CHECK',
      'Q_RESET_TIRE_PRESSURE_MONITOR',
    ];
    switch (this.inspectionService.inspection.name) {
      case 'Multi-Point Vehicle Health Check':
      case 'GM MPVI':
        this.inspectionService.title = this.inspectionService.inspection.name + ' WO:';
        this.inspectionService.inspection.reportType = 'GM';
        this.inspectionService.inspection.GMFullInspectionFlag = true;
        this.inspectionService.inspection.displayOnstarSection = true;
        break;
      case 'GM Express Inspection':
        this.inspectionService.title = this.inspectionService.inspection.name + ' WO:';
        this.inspectionService.inspection.reportType = 'GM';
        this.inspectionService.inspection.GMFullInspectionFlag = false;
        this.inspectionService.inspection.displayOnstarSection = true;
        break;
      case 'Multi-Point Vehicle Inspection (MPVI)':
        this.inspectionService.title = this.inspectionService.inspection.name + ' WO:';
        this.inspectionService.inspection.reportType = 'GEN';
        this.inspectionService.inspection.GMFullInspectionFlag = false;
        break;
      case 'Hyundai Vehicle Inspection Report':
        this.inspectionService.title = this.inspectionService.inspection.name + ' WO:';
        this.inspectionService.inspection.reportType = 'HYU';
        this.inspectionService.inspection.GMFullInspectionFlag = false;
        break;
      default:
        this.inspectionService.title = 'Vehicle Inspection WO:';
        this.inspectionService.inspection.reportType = 'VCR';
        this.inspectionService.inspection.GMFullInspectionFlag = false;
        this.inspectionService.inspection.displayOnstarSection = false;
        break;
    }

    if (this.inspectionService.inspection.categories) {
      for (let i = this.inspectionService.inspection.categories.length - 1; i >= 0; i--) {
        this.inspectionService.collapse[this.inspectionService.inspection.categories[i].categoryId] = false;
        this.inspectionService.collapseWalkaround[this.inspectionService.inspection.categories[i].categoryId] = false;
      }
      for (let i = 0; i < this.inspectionService.inspection.categories.length; i++) {
        if (this.inspectionService.inspection.categories[i].categoryName === 'OnStar Diagnostics') {
          this.inspectionService.inspection.onStarDiagnostics = JSON.parse(
            JSON.stringify(this.inspectionService.inspection.categories[i])
          );
          this.inspectionService.inspection.categories.splice(
            self.inspectionService.inspection.categories.findIndex(function (c: any) {
              return c.categoryName === self.inspectionService.inspection.onStarDiagnostics.categoryName;
            }),
            1
          );
        }
      }
      for (let i = 0; i < this.inspectionService.inspection.categories.length; i++) {
        if (this.inspectionService.inspection.categories[i].categoryName === 'Windshield & Wipers') {
          this.inspectionService.inspection.windshieldCracksChips = JSON.parse(
            JSON.stringify(this.inspectionService.inspection.categories[i])
          );
          this.inspectionService.inspection.windshieldCracksChips.tasks = this.inspectionService.inspection.categories[
            i
          ].tasks.filter(function (task: any) {
            if (windshieldChecks.includes(task.gmInspectionId) || windshieldChecks.includes(task.inspectionId)) {
              return task;
            }
          });
          if (this.inspectionService.inspection.windshieldCracksChips.tasks.length > 0) {
            this.inspectionService.inspection.windshieldCracksChips.tasks.forEach(function (task: any) {
              self.inspectionService.inspection.categories[i].tasks.splice(
                self.inspectionService.inspection.categories[i].tasks.findIndex(function (t: any) {
                  return t.taskName === task.taskName;
                }),
                1
              );
            });
          }
        }
      }
    }
    if (this.inspectionService.inspection.tireOther) {
      for (let i = this.inspectionService.inspection.tireOther.length - 1; i >= 0; i--) {
        this.inspectionService.collapse[this.inspectionService.inspection.tireOther[i].categoryId] = false;
        this.inspectionService.collapseWalkaround[this.inspectionService.inspection.tireOther[i].categoryId] = false;
      }
      if (this.inspectionService.inspection.tireOther[0].tasks.length > 3) {
        this.inspectionService.inspection.tireOtherCategories = JSON.parse(
          JSON.stringify(this.inspectionService.inspection.tireOther)
        );
        // eslint-disable-next-line max-len
        this.inspectionService.inspection.tireOtherCategories[0].tasks =
          this.inspectionService.inspection.tireOtherCategories[0].tasks.filter(function (task: any) {
            if (
              tireOtherCategoriesChecks.includes(task.gmInspectionId) ||
              tireOtherCategoriesChecks.includes(task.inspectionId)
            ) {
              if (task.gmInspectionId === 'TIRE_SEALANT_EXPIRATION') {
                const sealantExpirationDate = new Date('01/01/1901');
                sealantExpirationDate.setHours(0, 0, 0, 0);
                if (Date.parse(sealantExpirationDate.toString()) >= Date.parse(task.vehicleSealantExpirationDate)) {
                  task.vehicleSealantExpirationDate = null;
                }
              }
              return task;
            }
          });
        this.inspectionService.inspection.tireOtherGm = JSON.parse(
          JSON.stringify(this.inspectionService.inspection.tireOther)
        );

        // eslint-disable-next-line max-len
        this.inspectionService.inspection.tireOtherGm[0].tasks =
          this.inspectionService.inspection.tireOther[0].tasks.filter(function (task: any) {
            if (tireOtherGmChecks.includes(task.gmInspectionId) || tireOtherGmChecks.includes(task.inspectionId)) {
              return task;
            }
          });
        // eslint-disable-next-line max-len
        this.inspectionService.inspection.tireOther[0].tasks =
          this.inspectionService.inspection.tireOther[0].tasks.filter(function (task: any) {
            if (tireOtherChecks.includes(task.gmInspectionId) || tireOtherChecks.includes(task.inspectionId)) {
              return task;
            }
          });
      }
    }

    this.inspectionService.initialiseDualRearTires();
    this.inspectionService.updateTaskCounts();
  }

  collapse(collapse: any, tab: any) {
    if (tab === 0) {
      if (this.inspectionService.collapseWalkaroundAll === false) {
        Object.keys(collapse).forEach(function (key) {
          collapse[key] = true;
        });
      } else if (this.inspectionService.collapseWalkaroundAll === true) {
        Object.keys(collapse).forEach(function (key) {
          collapse[key] = false;
        });
      }
      this.inspectionService.collapseWalkaroundAll = !this.inspectionService.collapseWalkaroundAll;
    } else {
      if (this.inspectionService.collapseAll === false) {
        Object.keys(collapse).forEach(function (key) {
          collapse[key] = true;
        });
      } else if (this.inspectionService.collapseAll === true) {
        Object.keys(collapse).forEach(function (key) {
          collapse[key] = false;
        });
      }
      this.inspectionService.collapseAll = !this.inspectionService.collapseAll;
    }
  }

  //Rendered with the Tires card. Dual Rear Tires is excluded unless the toggle doesn't apply (GM/Hyundai).
  tireMeasurementCategories(categories: VehicleVipInspectionCategory[]): VehicleVipInspectionCategory[] {
    const toggleGovernsDualRearTires = this.inspectionService.isDualRearTiresToggleSupported();

    return (categories || []).filter(
      (category) =>
        !(toggleGovernsDualRearTires && category.categoryName === DUAL_REAR_TIRES_CATEGORY_NAME) &&
        category.tasks?.some((task) => task.displayTireTread)
    );
  }

  //setDualRearTiresEnabled owns the inspection$ emission, the same way onTaskActionChange does
  //for the task buttons — re-emitting here would run the unsaved-changes diff twice per click.
  onDualRearTiresToggleChange(toggleChange: MatSlideToggleChange): void {
    this.inspectionService.setDualRearTiresEnabled(toggleChange.checked);
  }

  nonTireMeasurementCategories(categories: VehicleVipInspectionCategory[]): VehicleVipInspectionCategory[] {
    return (categories || []).filter((category) => !category.tasks?.some((task) => task.displayTireTread));
  }

  inspectionChanged(inspection?: any) {
    if (inspection) {
      this.inspectionService.inspectionBehaviourSubject.next(inspection);
    } else {
      //Leverage this to call displaySaviIcon(). Populate displaySaviIconThatWay?
      //Need a way to deal with the checks for oem/enabled maybe put on service?
      //Prob need to modify call to pass in task.
      this.inspectionService.inspectionBehaviourSubject.next(this.inspectionService.inspection);
    }
  }

  deleteBodyReport() {
    this.inspectionService.deleteBodyReport();
  }

  populateInspectionWithVehicleDiagnosticsData() {
    //Populate GM Savis is called from 3 spots, Creating an inspection or in each if/else in getInspectionDetails.
    //This handles no inspection existing, or coming to an existing inspection.
    if (this.transactions$) {
      combineLatest([this.transactions$, this.vehicleDiagnostics$, this.workOrder$])
        .pipe(
          takeUntil(this.destroy$),
          map(([transactions, vehicleDiagnostics, workOrder]) => {
            if (vehicleDiagnostics.enabled) {
              let transactionServiceVisitId: string;
              switch (vehicleDiagnostics.oem) {
                case 'GM':
                  if (transactions.workOrder.statusId === 'R') {
                    transactionServiceVisitId = transactions.appointment.serviceVisitId;
                  } else {
                    transactionServiceVisitId = transactions.workOrder.serviceVisitId;
                  }

                  this.svcVehicleDiagnosticsRootFacade.serviceVisitsByVinStatus$
                    .pipe(
                      filter((serviceVistByVinStateStatus) => {
                        return (
                          serviceVistByVinStateStatus !== status.idle && serviceVistByVinStateStatus !== status.loading
                        );
                      }),
                      takeUntil(this.destroy$),
                      tap((serviceVistByVinStateStatus) => {
                        switch (serviceVistByVinStateStatus) {
                          case status.succeeded:
                            if (transactionServiceVisitId !== null) {
                              this.svcVehicleDiagnosticsRootFacade.updateLinkedServiceVisitInState(
                                transactionServiceVisitId
                              );
                              combineLatest([
                                this.svcVehicleDiagnosticsRootFacade.linkedServiceVisitMpvi$,
                                this.svcVehicleDiagnosticsRootFacade.linkedServiceVisitMpviStatus$.pipe(
                                  filter((mpviStatus) => mpviStatus !== status.idle && mpviStatus !== status.loading)
                                ),
                                this.svcVehicleDiagnosticsRootFacade.linkedServiceVisit$.pipe(
                                  filter((linkedServiceVisit: any) => linkedServiceVisit),
                                  map((linkedServiceVisit) => linkedServiceVisit)
                                ),
                              ])
                                .pipe(
                                  take(1),
                                  tap(
                                    ([linkedServiceVisitMpvi, linkedServiceVisitMpviStatus, linkedServiceVisitId]) => {
                                      switch (linkedServiceVisitMpviStatus) {
                                        case status.succeeded:
                                          if (
                                            this.svcVehicleDiagnosticsRootFacade.isWithinADay(
                                              linkedServiceVisitId.checkInDate,
                                              linkedServiceVisitId.checkInTime
                                            )
                                          ) {
                                            if (linkedServiceVisitMpvi) {
                                              if (
                                                this.inspectionService.gmSaviOverwriteDataCheck(linkedServiceVisitMpvi)
                                              ) {
                                                this.inspectionService.setMpviData(
                                                  linkedServiceVisitMpvi,
                                                  transactionServiceVisitId
                                                );
                                                this.updateTasks(true, workOrder).catch((err) =>
                                                  this.logUnlessSaveAborted(
                                                    'updateTasks (transaction MPVI) failed:',
                                                    err
                                                  )
                                                );
                                              }
                                              this.inspectionService.vehicleDiagnosticsId = transactionServiceVisitId;
                                            }
                                            this.inspectionService.isLoading = false;
                                          } else {
                                            this.inspectionService.isLoading = false;
                                          }
                                          break;
                                        case status.failed:
                                          this.inspectionService.isLoading = false;
                                          break;
                                        default:
                                          break;
                                      }
                                    }
                                  )
                                )
                                .subscribe();
                            } else {
                              this.inspectionService.isLoading = false;
                            }
                            break;
                          case status.failed:
                            this.inspectionService.isLoading = false;
                            break;
                          default:
                            break;
                        }
                      })
                    )
                    .subscribe();
                  break;
                default:
                  this.inspectionService.isLoading = false;
                  break;
              }
            } else {
              this.inspectionService.isLoading = false;
            }
          })
        )
        .subscribe();
    } else {
      combineLatest([
        this.serviceLaneFacade.selectedAppointment$.pipe(filter((selectedAppointment) => selectedAppointment !== null)),
        this.svcVehicleDiagnosticsRootFacade.recentServiceVisitBasedOnVin$,
        this.svcVehicleDiagnosticsRootFacade.linkedServiceVisitMpvi$,
        this.vehicleDiagnostics$,
        this.svcVehicleDiagnosticsRootFacade.linkedServiceVisitMpviStatus$,
        this.workOrder$,
      ])
        .pipe(
          takeUntil(this.destroy$),
          map(
            ([
              selectedAppointment,
              latestServiceVisit,
              linkedServiceVisitMpvi,
              vehicleDiagnostics,
              linkedServiceVisitMpviStatus,
              workOrder,
            ]) => {
              if (vehicleDiagnostics.enabled) {
                switch (vehicleDiagnostics.oem) {
                  case 'GM':
                    if (selectedAppointment.serviceVisitId !== null) {
                      if (this.inspectionService.vehicleDiagnosticsId !== selectedAppointment.serviceVisitId) {
                        switch (linkedServiceVisitMpviStatus) {
                          case status.succeeded:
                            if (
                              selectedAppointment.serviceVisitId === latestServiceVisit?.serviceVisitId &&
                              this.svcVehicleDiagnosticsRootFacade.isWithinADay(
                                latestServiceVisit.checkInDate,
                                latestServiceVisit.checkInTime
                              )
                            ) {
                              if (linkedServiceVisitMpvi) {
                                if (this.inspectionService.gmSaviOverwriteDataCheck(linkedServiceVisitMpvi)) {
                                  this.inspectionService.setMpviData(
                                    linkedServiceVisitMpvi,
                                    selectedAppointment.serviceVisitId
                                  );
                                  this.updateTasks(true, workOrder).catch((err) =>
                                    this.logUnlessSaveAborted('updateTasks (appointment MPVI) failed:', err)
                                  );
                                }
                                this.inspectionService.vehicleDiagnosticsId = selectedAppointment.serviceVisitId;
                              }
                              this.inspectionService.isLoading = false;
                            } else {
                              this.inspectionService.isLoading = false;
                            }
                            break;
                          case status.failed:
                            this.inspectionService.isLoading = false;
                            break;
                          default:
                            break;
                        }
                      } else {
                        this.inspectionService.isLoading = false;
                      }
                    } else {
                      this.inspectionService.isLoading = false;
                    }
                    break;
                  default:
                    this.inspectionService.isLoading = false;
                    break;
                }
              } else {
                this.inspectionService.isLoading = false;
              }
            }
          )
        )
        .subscribe();
    }
  }

  notifyAdvisor(authenticatedUser: TokenUser, workOrderId: number) {
    const dialogData: TwoActionDialogData = {
      actionTwoCaption: 'Send',
      actionOneCaption: 'Cancel',
      content: 'A message will be sent to the service advisor indicating that the inspection is ready for review.',
      title: 'Notify Advisor',
    };

    const dialogRef = this.dialog.open(TwoActionDialogComponent, {
      width: '400px',
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        const members: Member[] = [];

        const conversationQueryParameters: ConversationQueryParameters = new ConversationQueryParameters({
          transactionTypeId: 2,
          messageTypeId: 32,
          transactionId: workOrderId,
        });

        const event: Event = new Event({
          conversationId: null,
          memberId: authenticatedUser.id.toString(),
          eventTypeId: EventTypes.Message,
          guid: Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15),
          sentDate: new Date(),
          sent: false,
          sendToCustomer: false,
          id: Math.floor(Math.random() * (9999999 - 999999)) + 999999,
        });

        const conversation: Conversation = new Conversation({
          dmsMessageTypeId: 32,
          dmsTransactionTypeId: 2,
          dmsTransactionId: workOrderId,
          createdBy: authenticatedUser.id,
        });

        const member: Member = new Member({
          userId: authenticatedUser.id.toString(),
          avatarUrl: authenticatedUser.avatarUrl,
          firstName: authenticatedUser.firstName,
          lastName: authenticatedUser.lastName,
          nickname: authenticatedUser.firstName,
          isEmployee: true,
          isRead: true,
          isArchived: false,
          isExcluded: false,
          isPinnedToUi: false,
        });
        members.push(member);
        this.apiService
          .get<WorkOrder>(`v/1/fixed-operations/work-orders/${workOrderId}`, {
            params: { embed: 'customerAssociate' },
          })
          .pipe(
            tap((workOrder: WorkOrder) => {
              if (workOrder?.embedded?.customerAssociate) {
                event.content = `Vehicle inspection for RO ${workOrder.id} (${workOrder.embedded.customerAssociate.firstName} ${workOrder.embedded.customerAssociate.lastName} - ${workOrder.year} ${workOrder.vehicleMake} ${workOrder.series}) is complete and ready for review.`;
              } else {
                event.content = `Vehicle inspection for RO ${workOrder.id} is complete and ready for review.`;
              }
            }),
            switchMap((workOrder) => {
              return this.serviceLaneDropdownFacade.serviceAdvisors$.pipe(
                map((serviceAdvisors) => {
                  return serviceAdvisors.length > 0
                    ? serviceAdvisors.filter((serviceAdvisor) => serviceAdvisor.associateId === workOrder.employeeId)[0]
                    : null;
                }, take(1))
              );
            }),
            switchMap((serviceAdvisor) => {
              const userParams: UserQueryParameters = new UserQueryParameters({
                xselleratorEmployeeId: serviceAdvisor?.associateId,
                username: serviceAdvisor?.networkUserName,
              });

              return this.apiService.get<any>('quorum/users', { params: userParams });
            }),
            take(1)
          )
          .subscribe((qUser) => {
            if (qUser.length > 0) {
              const user = qUser[0];
              const member: Member = new Member({
                userId: user.id.toString(),
                avatarUrl: user.avatarUrl,
                firstName: user.firstName,
                lastName: user.lastName,
                nickname: user.firstName,
                isEmployee: true,
                isRead: true,
                isArchived: false,
                isExcluded: false,
                isPinnedToUi: false,
              });
              members.push(member);
              const optionalHeader = new HttpHeaders({ 'Use-Default-Template': 'false' });
              this.communicatorFacade.sendCommunicatorMessage(
                conversationQueryParameters,
                conversation,
                members,
                event,
                optionalHeader
              );
              const toastOptions: any = {
                message: `Service advisor notified.`,
                actionText: null,
                options: { duration: 3000 },
              };
              this.inspectionService.addToast(toastOptions);
            } else {
              const toastOptions: any = {
                message: `No Communicator account found for Service Advisor. No message sent.`,
                actionText: null,
                options: { duration: 5000 },
              };
              this.inspectionService.addToast(toastOptions);
            }
          });
      }
    });
  }

  /**
   * This method is via the document management app
   * @param destinationId
   * @param vin
   */

  // printHYUInspection(destinationId: string, vin: string) {
  //   const documentManagementBaseUrl: string = this.determineDocumentManagementURL(environment.environment);

  //   const vehicleId = this.inspectionService.vehicleId.toString();
  //   //const appointmentId = this.inspectionService.appointmentId.toString();
  //   const workOrderId = this.inspectionService.workOrderId;

  //   let queryParams: any = {
  //     vehicleId,
  //     //appointmentId,
  //     workOrderId,
  //     destinationId,
  //   };

  // combineLatest([
  //   this.authenticationStateService.selectAuthenticatedEmployee().pipe(
  //     filter((emp) => emp != null),
  //     take(1)
  //   ),
  //   this.getFuelType(vin),
  // ])
  //   .pipe(
  //     map(([authenticatedEmployee, fuelType]) => {
  //       queryParams = { ...queryParams, rooftopId: authenticatedEmployee.rooftopId };

  //       if (fuelType === 'EV') {
  //         queryParams = { ...queryParams, inspectionType: 'EV' };
  //       } else {
  //         queryParams = { ...queryParams, inspectionType: 'ICE' };
  //       }

  //       const queryString = new URLSearchParams(queryParams).toString();
  //       const fullUrl = `${documentManagementBaseUrl}?${queryString}`;

  //       // Create a hidden iframe
  //       const iframe = document.createElement('iframe');
  //       iframe.style.visibility = 'hidden';
  //       iframe.style.width = '0';
  //       iframe.style.height = '0';
  //       iframe.style.border = '0';
  //       iframe.src = fullUrl;

  //       // Remove iframe after 60 seconds if no response (safety timeout)
  //       setTimeout(() => {
  //         // Prevent cleanup from firing multiple times
  //         if (iframe.parentNode) {
  //           document.body.removeChild(iframe);
  //           // Remove the event listener to stop further events from being processed
  //           window.removeEventListener('message', handleMessage);
  //         }
  //       }, 60000);

  //       const handleMessage = (event: any) => {
  //         // Ensure the message is coming from DocumentManagement app
  //         if (event.origin === documentManagementBaseUrl && event.data === 'PRINT_COMPLETE') {
  //           document.body.removeChild(iframe);

  //           // Remove the event listener to stop further events from being processed
  //           window.removeEventListener('message', handleMessage);
  //         }
  //       };

  //       // Add the event listener
  //       window.addEventListener('message', handleMessage, false);

  //       // Append to DOM
  //       document.body.appendChild(iframe);
  //     })
  //   )
  //   .subscribe();
  // }

  /**
   * This method is via PL directly (to accommocate DMS issues)
   * @param vin
   */
  printHYUInspection(vin: string) {
    const printTarget = this.selectPdfPrintTarget();

    this.snackBar.open('Preparing inspection for printing..', null, {
      duration: 3500,
    });

    const vehicleId = this.inspectionService.vehicleId.toString();
    const workOrderId = this.inspectionService.workOrderId;

    let queryParams: any = {
      vehicleId,
      workOrderId,
    };

    combineLatest([
      this.authenticationStateService.selectAuthenticatedEmployee().pipe(
        filter((emp) => emp != null),
        take(1)
      ),
      this.getFuelType(vin),
    ])
      .pipe(
        map(([authenticatedEmployee, fuelType]) => {
          queryParams = { ...queryParams, rooftopId: authenticatedEmployee.rooftopId };

          if (fuelType === 'EV') {
            queryParams = { ...queryParams, inspectionType: 'EV' };
          } else {
            queryParams = { ...queryParams, inspectionType: 'ICE' };
          }

          if (vehicleId && workOrderId && authenticatedEmployee.rooftopId) {
            this.generateHyuPdf(
              vehicleId,
              workOrderId,
              authenticatedEmployee.rooftopId.toString(),
              queryParams.inspectionType?.toUpperCase(),
              printTarget
            );
          } else {
            console.warn(
              `Missing query params: ${!vehicleId ? vehicleId : ''}, ${!workOrderId ? workOrderId : ''}, ${
                !authenticatedEmployee.rooftopId ? authenticatedEmployee.rooftopId : ''
              }`
            );
          }
        })
      )
      .subscribe();
  }

  async generateHyuPdf(
    vehicleId: string,
    workOrderId: string,
    rooftopId: string,
    inspectionType: 'ICE' | 'EV',
    printTarget: PdfPrintTarget = { type: 'inline' }
  ) {
    try {
      let pdfBlob: Blob;

      switch (inspectionType) {
        case 'ICE':
          pdfBlob = await this.pdfService.generateHyuICEPdf(vehicleId, workOrderId, rooftopId);
          break;
        case 'EV':
          pdfBlob = await this.pdfService.generateHyuEVPdf(vehicleId, workOrderId, rooftopId);
          break;
        default:
          pdfBlob = await this.pdfService.generateHyuICEPdf(vehicleId, workOrderId, rooftopId);
          break;
      }

      this.openPdfAndPrint(pdfBlob, printTarget, 'hyundai-inspection.pdf');
    } catch (error) {
      console.error('Failed to generate PDF', error);
    }
  }

  printGMInspection(
    workOrder: WorkOrder,
    customerAddress: Address,
    appointment: ServiceAppointment,
    dealerCountryCode: string,
    xsTransactions: { appointment: ServiceAppointment; customerAddress: Address; workOrder: WorkOrder }
  ) {
    const printTarget = this.selectPdfPrintTarget();

    this.snackBar.open('Preparing inspection for printing..', null, {
      duration: 3500,
    });

    const vehicleId = this.inspectionService.vehicleId.toString();

    let queryParams: any = {
      vehicleId,
    };

    combineLatest([
      this.authenticationStateService.selectAuthenticatedEmployee().pipe(
        filter((emp) => emp != null),
        take(1)
      ),
      this.getFuelType(workOrder.serialNumber),
    ])
      .pipe(
        map(([authenticatedEmployee, fuelType]) => {
          queryParams = { ...queryParams, rooftopId: authenticatedEmployee.rooftopId };

          if (fuelType === 'EV') {
            queryParams = { ...queryParams, inspectionType: 'EV' };
          } else {
            queryParams = { ...queryParams, inspectionType: 'ICE' };
          }

          const embeddedInXsellerator = this.browserControlService.embeddedInXsellerator;

          if (vehicleId && authenticatedEmployee.rooftopId) {
            if (dealerCountryCode === 'CAN') {
              this.generateGMCanadaPdf(
                vehicleId,
                authenticatedEmployee.rooftopId.toString(),
                queryParams.inspectionType?.toUpperCase(),
                embeddedInXsellerator ? xsTransactions?.customerAddress : customerAddress,
                embeddedInXsellerator ? xsTransactions?.appointment : appointment,
                embeddedInXsellerator ? xsTransactions?.workOrder : workOrder,
                printTarget
              );
            } else if (dealerCountryCode === 'USA') {
              this.generateGMUSAPdf(
                vehicleId,
                authenticatedEmployee.rooftopId.toString(),
                queryParams.inspectionType?.toUpperCase(),
                embeddedInXsellerator ? xsTransactions?.customerAddress : customerAddress,
                embeddedInXsellerator ? xsTransactions?.appointment : appointment,
                embeddedInXsellerator ? xsTransactions?.workOrder : workOrder,
                printTarget
              );
            } else {
              console.warn(`Cannot print GM inspection for unsupported dealer country: ${dealerCountryCode}`);
            }
          } else {
            console.warn(
              `Missing query params: ${!vehicleId ? vehicleId : ''}, ${
                !authenticatedEmployee.rooftopId ? authenticatedEmployee.rooftopId : ''
              }`
            );
          }
        })
      )
      .subscribe();
  }

  async generateGMCanadaPdf(
    vehicleId: string,
    rooftopId: string,
    inspectionType: 'ICE' | 'EV',
    customerAddress: Address,
    appointment: ServiceAppointment,
    workOrder: WorkOrder,
    printTarget: PdfPrintTarget = { type: 'inline' }
  ) {
    try {
      let pdfBlob: Blob;

      switch (inspectionType) {
        case 'ICE':
          pdfBlob = await this.pdfService.generateGMCanadaICEPdf(
            vehicleId,
            rooftopId,
            customerAddress,
            appointment,
            workOrder
          );
          break;
        case 'EV':
          pdfBlob = await this.pdfService.generateGMCanadaEVPdf(
            vehicleId,
            rooftopId,
            customerAddress,
            appointment,
            workOrder
          );
          break;
        default:
          pdfBlob = await this.pdfService.generateGMCanadaICEPdf(
            vehicleId,
            rooftopId,
            customerAddress,
            appointment,
            workOrder
          );
          break;
      }

      this.openPdfAndPrint(pdfBlob, printTarget, 'gm-mpvi.pdf');
    } catch (error) {
      console.error('Failed to generate PDF', error);
    }
  }

  async generateGMUSAPdf(
    vehicleId: string,
    rooftopId: string,
    inspectionType: 'ICE' | 'EV',
    customerAddress: Address,
    appointment: ServiceAppointment,
    workOrder: WorkOrder,
    printTarget: PdfPrintTarget = { type: 'inline' }
  ) {
    try {
      let pdfBlob: Blob;

      switch (inspectionType) {
        case 'ICE':
          pdfBlob = await this.pdfService.generateGMUSAICEPdf(
            vehicleId,
            rooftopId,
            customerAddress,
            appointment,
            workOrder
          );
          break;
        case 'EV':
          pdfBlob = await this.pdfService.generateGMUSAEVPdf(
            vehicleId,
            rooftopId,
            customerAddress,
            appointment,
            workOrder
          );
          break;
        default:
          pdfBlob = await this.pdfService.generateGMUSAICEPdf(
            vehicleId,
            rooftopId,
            customerAddress,
            appointment,
            workOrder
          );
          break;
      }

      this.openPdfAndPrint(pdfBlob, printTarget, 'gm-mpvi.pdf');
    } catch (error) {
      console.error('Failed to generate PDF', error);
    }
  }

  // Gated preview of the data-driven inspection report; the existing print paths are unchanged.
  printInspectionPdfPreview(workOrder: WorkOrder): void {
    this.ensureInspectionSavedForPrint(workOrder)
      .then((isSaved) => (isSaved ? this.generateInspectionPdfPreview() : undefined))
      .catch((err) => this.logUnlessSaveAborted('Save before print failed:', err));
  }

  private ensureInspectionSavedForPrint(workOrder: WorkOrder): Promise<boolean> {
    if (!this.inspectionService.unsaved) {
      return Promise.resolve(true);
    }

    const dialogData: TwoActionDialogData = {
      actionTwoCaption: 'Save',
      actionOneCaption: 'Cancel',
      content: 'Unsaved changes will not display on the printed report. Would you like to save the inspection?',
      title: 'Save Inspection',
    };
    const dialogRef = this.dialog.open(TwoActionDialogComponent, {
      width: '400px',
      data: dialogData,
      disableClose: true,
    });

    return dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .toPromise()
      .then((shouldSave: boolean | undefined) => (shouldSave ? this.updateTasks(false, workOrder) : false));
  }

  private async generateInspectionPdfPreview(): Promise<void> {
    const printTarget = this.selectPdfPrintTarget();
    this.snackBar.open('Preparing inspection for printing..', null, { duration: 3500 });

    try {
      // The report header is loaded on inspection open and refreshed after a save without being
      // awaited, so a freshly created or just-saved inspection may not have it yet: fetch it here.
      const workOrderId: number = this.inspectionService.workOrderId;
      const report: { header?: VehicleInspectionReportHeader; quoteLines?: QuoteLine[] } | null =
        await this.inspectionService.getVcrReportData(workOrderId, this.inspectionService.quoteId);
      const pdfBlob = await this.inspectionReportPdfService.buildReportPdf(workOrderId, {
        header: report?.header ?? null,
        quoteLines: report?.quoteLines ?? [],
        brakeMeasurements: this.inspectionService.brakeMeasurements ?? [],
        treadDepthThresholds: this.inspectionService.treadDepthSettings ?? null,
        omittedTaskIds: this.inspectionService.getHiddenTireTasks().map((task) => task.taskId),
      });
      if (this.isDestroyed) {
        return;
      }
      this.openPdfAndPrint(pdfBlob, printTarget, 'inspection-report.pdf');
    } catch (error) {
      console.error('Failed to generate inspection PDF preview', error);
      this.snackBar.open('The inspection report could not be generated.', null, { duration: 5000 });
    }
  }

  // Shared print logic
  private openPdfAndPrint(pdfBlob: Blob, printTarget: PdfPrintTarget, fileName: string): void {
    this.pendingPdfPrintCleanup?.();

    if (printTarget.type === 'external') {
      this.showPdfReadyAction(pdfBlob, fileName);
      return;
    }

    const url = URL.createObjectURL(pdfBlob);
    const printIframe = document.createElement('iframe');
    printIframe.style.visibility = 'hidden';
    printIframe.style.position = 'fixed';
    printIframe.style.right = '0';
    printIframe.style.bottom = '0';
    printIframe.style.width = '0';
    printIframe.style.height = '0';
    printIframe.style.border = '0';

    let cleaned = false;
    let fallbackTimeout: ReturnType<typeof setTimeout> | undefined;
    let printDelay: ReturnType<typeof setTimeout> | undefined;

    const cleanup = () => {
      if (!cleaned) {
        cleaned = true;
        if (fallbackTimeout !== undefined) {
          clearTimeout(fallbackTimeout);
        }
        if (printDelay !== undefined) {
          clearTimeout(printDelay);
        }
        printIframe.contentWindow?.removeEventListener('afterprint', cleanup);
        window.removeEventListener('afterprint', cleanup);
        window.removeEventListener('beforeunload', cleanup);
        printIframe.onload = null;
        printIframe.onerror = null;
        printIframe.remove();
        URL.revokeObjectURL(url);

        if (this.pendingPdfPrintCleanup === cleanup) {
          this.pendingPdfPrintCleanup = undefined;
        }
      }
    };

    this.pendingPdfPrintCleanup = cleanup;

    window.addEventListener('afterprint', cleanup);
    window.addEventListener('beforeunload', cleanup);
    fallbackTimeout = setTimeout(cleanup, 300000);

    printIframe.onload = () => {
      if (cleaned) {
        return;
      }

      const iframeWindow = printIframe.contentWindow;

      if (!iframeWindow) {
        console.error('Failed to print PDF - iframe content window is unavailable');
        cleanup();
        return;
      }

      iframeWindow.addEventListener('afterprint', cleanup);

      printDelay = setTimeout(() => {
        if (cleaned) {
          return;
        }

        try {
          iframeWindow.focus();
          iframeWindow.print();
        } catch (error) {
          console.error('Failed to trigger print:', error);
          cleanup();
        }
      }, 500);
    };

    printIframe.onerror = () => {
      if (cleaned) {
        return;
      }

      console.error('Failed to load PDF for printing');
      cleanup();
    };

    try {
      printIframe.src = url;
      document.body.appendChild(printIframe);
    } catch (error) {
      console.error('Failed to prepare PDF for printing:', error);
      cleanup();
    }
  }

  private selectPdfPrintTarget(): PdfPrintTarget {
    const navigatorWithPdfSupport = navigator as Navigator & { pdfViewerEnabled?: boolean };

    return requiresExternalPdfViewer(navigatorWithPdfSupport) ? { type: 'external' } : { type: 'inline' };
  }

  private showPdfReadyAction(pdfBlob: Blob, fileName: string): void {
    const url = URL.createObjectURL(pdfBlob);
    let cleaned = false;

    const cleanup = (): void => {
      if (cleaned) {
        return;
      }

      cleaned = true;
      URL.revokeObjectURL(url);

      if (this.pendingPdfPrintCleanup === cleanup) {
        this.pendingPdfPrintCleanup = undefined;
      }
    };

    this.pendingPdfPrintCleanup = cleanup;

    const readySnackBar = this.snackBar.open('The inspection is ready to print.', 'Open PDF');

    readySnackBar
      .onAction()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(() => {
        if (cleaned) {
          return;
        }

        const pdfWindow = window.open(url, '_blank');

        if (!pdfWindow) {
          cleanup();
          this.downloadPdf(pdfBlob, fileName);
          return;
        }

        // The new tab owns the loaded PDF now. Keep the object URL available long
        // enough for slow mobile viewers, but do not revoke it if this component closes.
        if (this.pendingPdfPrintCleanup === cleanup) {
          this.pendingPdfPrintCleanup = undefined;
        }
        setTimeout(cleanup, 300000);
      });

    readySnackBar
      .afterDismissed()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(({ dismissedByAction }) => {
        if (!dismissedByAction) {
          cleanup();
        }
      });
  }

  private downloadPdf(pdfBlob: Blob, fileName: string): void {
    const url = URL.createObjectURL(pdfBlob);
    const downloadLink = document.createElement('a');
    downloadLink.href = url;
    downloadLink.download = fileName;
    downloadLink.style.display = 'none';
    document.body.appendChild(downloadLink);
    downloadLink.click();
    downloadLink.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60000);

    this.snackBar.open('The inspection PDF was downloaded. Open it from Downloads to print.', null, {
      duration: 8000,
    });
  }

  determineDocumentManagementURL(environment: string) {
    switch (environment) {
      case 'development':
        return 'http://localhost:4201';
      case 'staging':
        return 'https://staging-document-management.xsellerator.com';
      case 'production':
        return 'https://document-management.xsellerator.com';
      default:
        return 'https://staging-document-management.xsellerator.com';
    }
  }
  // PLEASE KEEP FOR FUTURE USE
  // generateInspectionPdf(
  //   storageId = 'storage',
  //   removeSelectors: string[] = ['#quoteHeader', '#quoteContainer', '#quoteTotal'],
  //   delayMs = 5000,
  //   inspectionPost?: boolean
  // ): void {
  //   const self = this;
  //   self.printingReport = true;

  //   setTimeout(() => {
  //     const containerEl = document.getElementById(storageId);
  //     if (!containerEl) {
  //       console.warn(`No element with id="${storageId}" found!`);
  //       self.printingReport = false;
  //       return;
  //     }

  //     // ----------------------------------
  //     // If inspectionPost === false → Generate PDF
  //     // ----------------------------------
  //     if (!inspectionPost) {
  //       const divContents = containerEl.innerHTML;
  //       const tempDiv = document.createElement('div');
  //       tempDiv.id = 'TempVcrReport';
  //       tempDiv.className = 'printMe';
  //       tempDiv.style.setProperty('display', 'block', 'important');
  //       tempDiv.style.position = 'absolute';
  //       tempDiv.style.left = '-9999px';

  //       tempDiv.innerHTML = divContents;
  //       document.body.appendChild(tempDiv);

  //       removeSelectors.forEach((selector) => {
  //         const el = tempDiv.querySelector(selector);
  //         if (el) {
  //           el.remove();
  //         }
  //       });

  //       html2canvas(tempDiv, {
  //         useCORS: true,
  //         allowTaint: false,
  //         backgroundColor: '#fff',
  //         scale: 2,
  //       })
  //         .then((canvas) => {
  //           const imgData = canvas.toDataURL('image/jpeg', 1.0);
  //           const pdf = new jsPDF('p', 'pt', 'a4');
  //           const pdfWidth = pdf.internal.pageSize.getWidth();
  //           const pdfHeight = pdf.internal.pageSize.getHeight();

  //           pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
  //           const pdfBase64 = pdf.output('datauristring').split(',')[1];

  //           self.workOrderDocumentService
  //             .updateWorkOrderDocument(
  //               self.inspectionService.workOrderId.toString(),
  //               pdfBase64,
  //               {
  //                 inspectionTypeId: self.inspectionService?.inspection?.inspectionTypeId || null,
  //                 inspectionName: self.inspectionService.inspection.name,
  //               },
  //               self.inspectionService.inspection.currentUser
  //             )
  //             .pipe(take(1))
  //             .subscribe({
  //               next: (updatedDoc) => {
  //                 if (updatedDoc?.metadata?.documentId) {
  //                   self.documentId = updatedDoc.SK;
  //                 }
  //                 document.body.removeChild(tempDiv);
  //                 self.printingReport = false;
  //               },
  //               error: (err) => {
  //                 console.error('Failed to upload PDF:', err);
  //                 document.body.removeChild(tempDiv);
  //                 self.printingReport = false;
  //               },
  //             });
  //         })
  //         .catch((error) => {
  //           console.error('html2canvas error:', error);
  //           document.body.removeChild(tempDiv);
  //           self.printingReport = false;
  //         });

  //       // ----------------------------------
  //       // If inspectionPost === true → No PDF creation
  //       // ----------------------------------
  //     } else {
  //       const pdfBase64 = '';

  //       self.workOrderDocumentService
  //         .updateWorkOrderDocument(
  //           self.inspectionService.workOrderId.toString(),
  //           pdfBase64,
  //           {
  //             inspectionTypeId: self.inspectionService?.inspection?.inspectionTypeId || null,
  //             inspectionName: self.inspectionService.inspection.name,
  //           },
  //           self.inspectionService.inspection.currentUser
  //         )
  //         .pipe(take(1))
  //         .subscribe({
  //           next: (updatedDoc) => {
  //             if (updatedDoc?.metadata?.documentId) {
  //               self.documentId = updatedDoc.SK;
  //             }
  //             self.printingReport = false;
  //           },
  //           error: (err) => {
  //             console.error('Failed to update work order document:', err);
  //             self.printingReport = false;
  //           },
  //         });
  //     }
  //   }, delayMs);
  // }
}
